import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import {
  Container, Typography, Box, Chip, Button, Grid, Paper,
  Divider, List, ListItem, ListItemIcon, ListItemText,
  Accordion, AccordionSummary, AccordionDetails, Stack
} from '@mui/material'
import {
  CallMade, ExpandMore, Article as ArticleIcon,
  GitHub, Code, Share, TrendingUp, CalendarToday, AccessTime
} from '@mui/icons-material'

// For articles/technical writing in your portfolio
export default function ArticleDisplay({ project }) {
  const navigate = useNavigate()

  if (!project) {
    return (
      <Container>
        <Typography variant='h4'>Article Not Found</Typography>
        <Button onClick={() => navigate(-1)} variant='contained'>
          Back to Portfolio
        </Button>
      </Container>
    )
  }

  const isArticle = project.category === 'writing'

  return (
    <Container sx={{ pt: 8, pb: 12 }}>
      {/* Header */}
      <Box sx={{ mb: 6 }}>
        <Chip
          icon={<ArticleIcon />}
          label={project.medium || 'Technical Article'}
          sx={{ bgcolor: '#18C9CD', color: '#fff', mb: 2 }}
        />
        
        <Typography variant='h3' gutterBottom color='#fff' role='heading'>
          {project.name}
        </Typography>
        
        <Typography variant='h5' color='#18C9CD' sx={{ mb: 3 }}>
          {project.tagline}
        </Typography>

        {/* Article Meta */}
        <Stack direction='row' spacing={1} sx={{ mb: 3 }}>
          {project.publishedDate && (
            <>
              <CalendarToday />
              <Typography variant='body2' color='#ccc'>
                {project.publishedDate}
              </Typography>
            </>
          )}
          {project.readTime && (
            <>
              <AccessTime />
              <Typography variant='body2' color='#ccc'>
                {project.readTime}
              </Typography>
            </>
          )}
          <Typography variant='body2' color='#ccc'>
            {project.role}
          </Typography>
        </Stack>

        {/* Tags */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          {project.badges?.map((badge, i) => (
            <Chip
              key={i}
              label={badge.label}
              color={badge.color}
              size='small'
            />
          ))}
        </Box>

        {/* Action Buttons */}
        <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap>
          {project.url && (
            <Button
              variant='contained'
              href={project.url}
              target='_blank'
              endIcon={<CallMade />}
              sx={{ bgcolor: '#18C9CD', '&:hover': { bgcolor: '#18C9CD', opacity: 0.9 } }}
            >
              {project.navigation || 'Read Article'}
            </Button>
          )}
          {project.githubRepo && (
            <Button
              variant='outlined'
              href={project.githubRepo}
              target='_blank'
              startIcon={<GitHub />}
              sx={{ borderColor: '#18C9CD', color: '#18C9CD' }}
            >
              View Code
            </Button>
          )}
          {project.liveDemoUrl && (
            <Button
              variant='outlined'
              href={project.liveDemoUrl}
              target='_blank'
              startIcon={<Code />}
              sx={{ borderColor: '#18C9CD', color: '#18C9CD' }}
            >
              Live Demo
            </Button>
          )}
        </Stack>

        {/* Engagement Metrics */}
        {project.metrics && (
          <Grid container spacing={2} sx={{ mt: 4 }}>
            {project.metrics.map((metric, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    bgcolor: '#111',
                    border: '1px solid #18C9CD'
                  }}
                >
                  <Typography variant='h4' color='#18C9CD'>
                    {metric.value}
                  </Typography>
                  <Typography variant='caption'>{metric.label}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Divider sx={{ my: 6, bgcolor: '#18c9cd' }} />

      {/* Abstract/Introduction */}
      {project.abstract && (
        <Box sx={{ mb: 6 }}>
          <Typography variant='h4' gutterBottom color='#fff'>
            Abstract
          </Typography>
          <Paper sx={{ p: 3, bgcolor: '#011640'}}>
            <Typography variant='h4' sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              {project.abstract}
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Key Findings */}
      {project.keyFindings && (
        <Box sx={{ mb: 8 }}>
          <Typography variant='h4' gutterBottom color='#fff'>
            Key Findings
          </Typography>
          <Grid container spacing={2}>
            {project.keyFindings.map((finding, index) => (
              <Grid item xs={12} md={6} key={index} gutterBottom>
                <Paper
                  sx={{
                    p: 2.5,
                    bgcolor: '#011640',
                    color: '#ffd',
                    border: '1px solid #18C9CD',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <TrendingUp sx={{ color: '#18C9CD', mr: 2, fontSize: 28 }} />
                  <Typography variant='body1' gutterBottom>{finding}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Problem Statement */}
      {project.problem && (
        <Box sx={{ mb: 6 }}>
          <Typography variant='h4' gutterBottom color='#fff'>
            The Problem
          </Typography>
          <Typography
            variant='body1'
            sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}
          >
            {project.problem}
          </Typography>
        </Box>
      )}

      {/* Methodology */}
      {project.methodology && (
        <Box sx={{ mb: 6 }}>
          <Typography variant='h4' gutterBottom color='#fff'>
            Methodology
          </Typography>
          <Typography
            variant='body1'
            sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}
          >
            {project.methodology}
          </Typography>
        </Box>
      )}

      {/* Findings/Analysis */}
      {project.findings && (
        <Box sx={{ mb: 6 }}>
          <Typography variant='h4' gutterBottom color='#fff'>
            Detailed Findings
          </Typography>
          <Typography
            variant='body1'
            textAlign='left'
            sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}
          >
            {project.findings}
          </Typography>
        </Box>
      )}

      {/* Code Examples */}
      {project.codeExamples && project.codeExamples.length > 0 && (
        <Box sx={{ mb: 6 }}>
          <Typography variant='h4' gutterBottom color='#fff'>
            Code Examples
          </Typography>
          {project.codeExamples.map((example, index) => (
            <Accordion
              key={index}
              defaultExpanded={index === 0}
              sx={{
                bgcolor: '#111',
                border: '1px solid #18C9CD',
                mb: 2,
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: '#fff' }} />}
                sx={{ '& .MuiAccordionSummary-content': { my: 2 } }}
              >
                <Typography variant='h6' color='#fff'>
                  {example.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {/* Before */}
                  <Grid item xs={12} md={example.after ? 6 : 12}>
                    {example.before && (
                      <>
                        <Typography
                          variant='subtitle2'
                          color='#ff4444'
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          {example.after ? '❌ Before' : '📝 Code'}
                        </Typography>
                        <Paper
                          sx={{
                            p: 2,
                            bgcolor: example.after ? '#1a0000' : '#0a2540',
                            border: example.after ? '1px solid #ff4444' : '1px solid #18C9CD',
                            overflow: 'auto',
                            maxHeight: 400
                          }}
                        >
                          <pre
                            style={{
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                              fontFamily: 'monospace',
                              fontSize: '0.9rem',
                              color: '#fff',
                              lineHeight: 1.6
                            }}
                          >
                            {example.before}
                          </pre>
                        </Paper>
                      </>
                    )}
                  </Grid>

                  {/* After */}
                  {example.after && (
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant='subtitle2'
                        color='#4ade80'
                        gutterBottom
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        ✓ After
                      </Typography>
                      <Paper
                        sx={{
                          p: 2,
                          bgcolor: '#001a00',
                          border: '1px solid #4ade80',
                          overflow: 'auto',
                          maxHeight: 400
                        }}
                      >
                        <pre
                          style={{
                            margin: 0,
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                            color: '#fff',
                            lineHeight: 1.6
                          }}
                        >
                          {example.after}
                        </pre>
                      </Paper>
                    </Grid>
                  )}
                </Grid>

                {/* Explanation */}
                {example.explanation && (
                  <Paper
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: '#0a2540',
                      border: '1px solid #18C9CD'
                    }}
                  >
                    <Typography variant='body2' sx={{ lineHeight: 1.7 }}>
                      <strong>Explanation:</strong> {example.explanation}
                    </Typography>
                  </Paper>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Solution/Implementation */}
      {project.solution && (
        <Box sx={{ mb: 6 }}>
          <Typography variant='h4' gutterBottom color='#fff'>
            Solution & Implementation
          </Typography>
          <Typography
            variant='body1'
            sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}
          >
            {project.solution}
          </Typography>
        </Box>
      )}

      {/* Impact */}
      {project.impact && (
        <Box sx={{ mb: 6 }}>
          <Typography variant='h4' gutterBottom color='#fff'>
            Impact & Results
          </Typography>
          <Typography
            variant='body1'
            sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}
          >
            {project.impact}
          </Typography>
        </Box>
      )}

      {/* Lessons Learned */}
      {project.lessons && (
        <Box sx={{ mb: 6 }}>
          <Typography variant='h4' gutterBottom color='#fff'>
            Lessons Learned
          </Typography>
          <Typography
            variant='body1'
            sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}
          >
            {project.lessons}
          </Typography>
        </Box>
      )}

      {/* Future Work */}
      {project.futureWork && project.futureWork.length > 0 && (
        <Box sx={{ mb: 6 }}>
          <Typography variant='h4' gutterBottom color='#fff'>
            Future Work
          </Typography>
          <List>
            {project.futureWork.map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#18C9CD'
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant='body1'>{item}</Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* References */}
      {project.references && project.references.length > 0 && (
        <Box sx={{ mb: 6 }}>
          <Typography variant='h4' gutterBottom color='#fff'>
            References & Resources
          </Typography>
          <Paper sx={{ p: 3, bgcolor: '#111', border: '1px solid #333' }}>
            <List dense>
              {project.references.map((ref, index) => (
                <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                  <Typography variant='body2' color='#ccc'>
                    {index + 1}. {ref}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Box>
      )}

      {/* Screenshots */}
      {project.screenshots && project.screenshots.length > 0 && (
        <Box sx={{ mb: 6 }}>
          <Typography variant='h4' gutterBottom color='#fff'>
            Visuals
          </Typography>
          <Grid container spacing={2}>
            {project.screenshots.map((src, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper
                  sx={{
                    p: 1,
                    bgcolor: '#111',
                    border: '1px solid #333'
                  }}
                >
                  <img
                    src={src}
                    alt={`${project.name} visual ${index + 1}`}
                    style={{
                      width: '100%',
                      borderRadius: 4,
                      display: 'block'
                    }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Divider sx={{ my: 6, bgcolor: '#333' }} />

      {/* Call to Action */}
      <Paper
        sx={{
          p: 4,
          textAlign: 'center',
          bgcolor: '#0a2540',
          border: '1px solid #18C9CD'
        }}
      >
        <Typography variant='h5' gutterBottom color='#fff'>
          Found this helpful?
        </Typography>
        <Typography variant='body1' color='#ccc' sx={{ mb: 3 }}>
          Share it with others or reach out to discuss accessibility!
        </Typography>
        <Stack direction='row' spacing={2} justifyContent='center' flexWrap='wrap' useFlexGap>
          {project.url && (
            <Button
              variant='outlined'
              href={project.url}
              target='_blank'
              startIcon={<Share />}
              sx={{ borderColor: '#18C9CD', color: '#18C9CD' }}
            >
              Share Article
            </Button>
          )}
          <Button
            variant='contained'
            onClick={() => navigate('/')}
            sx={{ bgcolor: '#18C9CD' }}
          >
            Back to Portfolio
          </Button>
        </Stack>
      </Paper>
    </Container>
  )
}